// CrossSheetLinksSection — bottom-of-block-properties section som
// listar cross-sheet-connectors där det valda blocket ingår och
// exponerar en "Link to adjacent sheet"-knapp som startar
// linking-mode med blocket pre-pickat som source.
//
// Verbatim extract från PropertiesPanel.tsx (POST_QA_SUBPLAN E.1
// cleanup). Ingen logikändring — bara förflyttning till egen fil.

import { ArrowUpDown, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import type { WorkflowBlock, WorkflowSheet, CrossSheetConnector } from '@/types/workflow';

export function CrossSheetLinksSection({
  block, sheets, activeSheetIndex, crossSheetConnectors,
  onStart, onDelete, onNavigateToSheet,
}: {
  block: WorkflowBlock;
  sheets: WorkflowSheet[];
  activeSheetIndex: number;
  crossSheetConnectors: CrossSheetConnector[];
  onStart: (blockId: string) => void;
  onDelete?: (id: string) => void;
  onNavigateToSheet?: (index: number) => void;
}) {
  const links = crossSheetConnectors.filter(c =>
    (c.fromSheetIndex === activeSheetIndex && c.fromBlockId === block.id) ||
    (c.toSheetIndex === activeSheetIndex && c.toBlockId === block.id)
  );

  return (
    <div className="space-y-2 pt-4 border-t border-border" data-guide="properties-cross-sheet">
      <div className="flex items-center gap-2">
        <ArrowUpDown className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">
          Cross-sheet links
        </Label>
      </div>

      {links.length > 0 ? (
        <ul className="space-y-1">
          {links.map(link => {
            const isOutgoing = link.fromSheetIndex === activeSheetIndex && link.fromBlockId === block.id;
            const otherSheetIndex = isOutgoing ? link.toSheetIndex : link.fromSheetIndex;
            const otherBlockId = isOutgoing ? link.toBlockId : link.fromBlockId;
            const otherSheet = sheets[otherSheetIndex];
            const otherBlockName = otherSheet?.blocks.find(b => b.id === otherBlockId)?.config.name ?? 'Block';
            const direction: 'up' | 'down' = otherSheetIndex < activeSheetIndex ? 'up' : 'down';
            return (
              <li
                key={link.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs bg-muted/40 hover:bg-muted transition-colors"
              >
                {direction === 'up' ? (
                  <ArrowUp className="w-3 h-3 flex-shrink-0 text-primary" aria-hidden="true" />
                ) : (
                  <ArrowDown className="w-3 h-3 flex-shrink-0 text-primary" aria-hidden="true" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{otherBlockName}</div>
                  <div className="truncate text-muted-foreground text-[10px]">
                    {isOutgoing ? 'to' : 'from'} {otherSheet?.name ?? `Sheet ${otherSheetIndex + 1}`}
                  </div>
                </div>
                {onNavigateToSheet && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-[10px]"
                    onClick={() => onNavigateToSheet(otherSheetIndex)}
                    aria-label={`Go to ${otherSheet?.name ?? 'other sheet'}`}
                  >
                    Go
                  </Button>
                )}
                {onDelete && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-destructive hover:text-destructive"
                    onClick={() => onDelete(link.id)}
                    aria-label="Delete cross-sheet link"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">
          No cross-sheet links yet. Use the button below to connect this block to one on an adjacent sheet.
        </p>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={() => onStart(block.id)}
        className="w-full gap-2 mt-2"
      >
        <ArrowUpDown className="w-3.5 h-3.5" aria-hidden="true" />
        Link to adjacent sheet
      </Button>
    </div>
  );
}
